import flatpickr from "flatpickr";
import Holidays from "date-holidays";
import weekSelect from "./utils/weekSelect";

const pickers = {};

function addClearButton(element, pickerInstance) {
  if (!element.hasAttribute("required")) {
    const container = element.closest(".controls");
    if (!container) return;
    const clearButton = document.createElement("button");
    clearButton.innerHTML = '<i class="fa fa-xmark"></i>';
    clearButton.type = "button";
    clearButton.className = "btn btn-date-clear";
    clearButton.addEventListener("click", function () {
      pickerInstance.clear();
    });
    container.appendChild(clearButton);
  }
}

function safeGetPicker(id) {
  return pickers[id] || null;
}

function safePickerSet(id, option, value) {
  const picker = safeGetPicker(id);
  if (!picker) {
    return;
  }
  if (value === undefined) return;
  if (typeof picker.set === "function") {
    picker.set(option, value);
  }
}

function firstDayOfWeek(dateObject, firstDayOfWeekIndex) {
  const dayOfWeek = dateObject.getDay();
  const firstDayOfWeek = new Date(dateObject);
  const diff =
    dayOfWeek >= firstDayOfWeekIndex
      ? dayOfWeek - firstDayOfWeekIndex
      : 6 - dayOfWeek;
  firstDayOfWeek.setDate(dateObject.getDate() - diff);
  firstDayOfWeek.setHours(0, 0, 0, 0);
  return firstDayOfWeek;
}

function lastDayOfTheWeek(dateObject, lastDayOfWeekIndex) {
  const lastDay = new Date(dateObject);
  const diff = (lastDayOfWeekIndex + (7 - dateObject.getDay())) % 7;
  lastDay.setDate(dateObject.getDate() + diff);
  lastDay.setHours(0, 0, 0, 0);
  return lastDay;
}

export function initializeDatePicker(element, country, extras) {
  const holidays = new Holidays(country, { types: ["public"] });

  const instance = flatpickr(element, {
    altFormat: extras.dateFormat,
    altInput: true,
    allowInput: true,
    appointmentDelivery: false,
    onDayCreate: function (dObj, dStr, fp, dayElem) {
      const year = dayElem.dateObj.getFullYear();
      holidays.getHolidays(year).forEach(function (h) {
        const holyDate = new Date(h.date);
        const dayYmd = `${dayElem.dateObj.getFullYear()}-${dayElem.dateObj.getMonth()}-${dayElem.dateObj.getDate()}`;
        const holyYmd = `${holyDate.getFullYear()}-${holyDate.getMonth()}-${holyDate.getDate()}`;
        if (dayYmd === holyYmd) {
          dayElem.className += " higlighted-holiday";
        }
      });
    },
    onChange: function (selectedDates) {
      const currentId = element.id;
      const currentDate = safeGetPicker(currentId)?.selectedDates?.[0];
      if (!currentDate) return;

      if (element.dataset.endDate) {
        safePickerSet(`id_${element.dataset.endDate}`, "minDate", currentDate);
      }
      if (element.dataset.startDate) {
        safePickerSet(
          `id_${element.dataset.startDate}`,
          "maxDate",
          currentDate,
        );
      }
    },
    onOpen: function (selectedDates, dateStr, instance) {
      if (instance.appointmentDelivery) {
        this.set("maxDate", null);
      }
      this.altInput.readOnly = true;

      const currentId = element.id;
      const currentDate = safeGetPicker(currentId)?.selectedDates?.[0];
      if (!currentDate) return;

      if (element.dataset.endDate && !instance.appointmentDelivery) {
        safePickerSet(`id_${element.dataset.endDate}`, "minDate", currentDate);
      }
      if (element.dataset.startDate) {
        safePickerSet(
          `id_${element.dataset.startDate}`,
          "maxDate",
          currentDate,
        );
      }
    },
    onClose: function () {
      this.altInput.readOnly = false;
      this.altInput.blur();
    },
  });

  pickers[element.id] = instance;
  addClearButton(element, instance);
}

export function initializeDateRangePicker(element, extras) {
  const instance = flatpickr(element, {
    mode: "range",
    altFormat: extras.dateFormat,
    onClose: (selectedDates, dateStr, instance) => {
      if (selectedDates.length === 1) {
        instance.setDate([selectedDates[0], selectedDates[0]]);
      }
    },
    altInput: true,
  });
  pickers[element.id] = instance;
  addClearButton(element, instance);
}

export function initializeTimePicker(element, extras) {
  const instance = flatpickr(element, {
    enableTime: true,
    noCalendar: true,
    dateFormat: "H:i",
    altFormat: extras.timeFormat,
    altInput: true,
    time_24hr: extras.timeFormat === "H:i",
    onChange: function (selectedDates) {
      const currentDate = selectedDates?.[0];
      if (!currentDate) return;

      if (element.dataset.endTime) {
        safePickerSet(`id_${element.dataset.endTime}`, "minTime", currentDate);
      }
      if (element.dataset.startTime) {
        safePickerSet(
          `id_${element.dataset.startTime}`,
          "maxTime",
          currentDate,
        );
      }
    },
  });
  pickers[element.id] = instance;
  addClearButton(element, instance);
}

export function initializeDateTimePicker(element, extras) {
  const instance = flatpickr(element, {
    enableTime: true,
    altFormat: "{extras.dateFormat} {extras.timeFormat}",
    altInput: true,
    time_24hr: extras.timeFormat === "H:i",
  });
  pickers[element.id] = instance;
  addClearButton(element, instance);
}

export function initializeDateTimeFields(rootElement, extras) {
  rootElement.querySelectorAll(".dateinput").forEach((element) => {
    let country = "";
    try {
      const countryElem = document.getElementById("country-code");
      country = countryElem?.value || "";
      const destinationAddress = document.getElementById(
        "id_destination_address",
      );
      const addressAPIElem = document.getElementById("address-api");
      if (destinationAddress && addressAPIElem?.value) {
        destinationAddress.addEventListener("change", async function () {
          const response = await fetch(
            addressAPIElem.replace("0", destinationAddress.firstChild.value),
          );
          const item = await response.json();
          country = item["country_code"];
          initializeDatePicker(element, country, extras);
        });
      }
    } catch (e) {
      country = document.getElementById("country-code")?.value || "";
    } finally {
      initializeDatePicker(element, country, extras);
    }
  });

  rootElement.querySelectorAll(".daterangeinput").forEach((element) => {
    initializeDateRangePicker(element, extras);
  });

  rootElement.querySelectorAll(".timeinput").forEach((element) => {
    initializeTimePicker(element, extras);
  });

  rootElement.querySelectorAll(".datetimeinput").forEach((element) => {
    initializeDateTimePicker(element, extras);
  });
}

export function initializeDashboardDateRangePicker(element, extras) {
  const instance = flatpickr(element, {
    mode: "range",
    dateFormat: extras.dateFormat,
    altFormat: extras.dateFormat,
    altInput: true,
    defaultDate: [
      firstDayOfWeek(new Date(), 1),
      lastDayOfTheWeek(new Date(), 7),
    ],
    locale: { firstDayOfWeek: 1 },
    plugins: [new weekSelect({})],
  });
  return instance;
}

export function initializeAllDayCheckbox(rootElement) {
  rootElement.querySelectorAll(".time-range-container").forEach((container) => {
    const checkbox = container.querySelector('input[type="checkbox"]');
    if (!checkbox) return;
    const inputs = container.querySelectorAll(".timeinput.flatpickr-input");
    if (inputs.length !== 2) {
      checkbox.closest(".checkbox-container")?.remove();
      return;
    }

    const fromInput = inputs[0];
    const toInput = inputs[1];

    const setDisabledAttr = (id, disabled) => {
      const picker = safeGetPicker(id);
      if (!picker) return;
      if (disabled) picker._input?.setAttribute("disabled", "disabled");
      else picker._input?.removeAttribute("disabled");
    };

    if (checkbox.checked) {
      setDisabledAttr(fromInput.id, true);
      setDisabledAttr(toInput.id, true);
    }

    checkbox.addEventListener("change", function (e) {
      const fromPicker = safeGetPicker(fromInput.id);
      const toPicker = safeGetPicker(toInput.id);
      if (e.target.checked) {
        if (fromPicker && typeof fromPicker.setDate === "function")
          fromPicker.setDate(null);
        if (toPicker && typeof toPicker.setDate === "function")
          toPicker.setDate(null);
        setDisabledAttr(fromInput.id, true);
        setDisabledAttr(toInput.id, true);
      } else {
        setDisabledAttr(fromInput.id, false);
        setDisabledAttr(toInput.id, false);
      }
    });
  });
}

document.addEventListener("projectsInitialized", function () {
  initializeDateTimeFields(document, dataTimeFieldFormats);
  initializeAllDayCheckbox(document);

  for (const id in pickers) {
    const picker = pickers[id];
    if (!picker || !picker.element) continue;

    const element = picker.element;
    const selected = picker.selectedDates?.[0];
    if (!selected) continue;

    const trySet = (refId, option, value) => {
      const targetId = `id_${refId}`;
      const targetPicker = safeGetPicker(targetId);
      if (targetPicker && typeof targetPicker.set === "function") {
        targetPicker.set(option, value);
      }
    };

    if (element.dataset.endDate)
      trySet(element.dataset.endDate, "minDate", selected);
    if (element.dataset.startDate)
      trySet(element.dataset.startDate, "maxDate", selected);
    if (element.dataset.endTime)
      trySet(element.dataset.endTime, "minTime", selected);
    if (element.dataset.startTime)
      trySet(element.dataset.startTime, "maxTime", selected);
  }
});

export function parseDate(dateStr, dateFormat) {
  return flatpickr.parseDate(dateStr, dateFormat);
}

export function formatDate(dateObj, dateFormat) {
  return flatpickr.formatDate(dateObj, dateFormat);
}

export default {
  initializeDatePicker,
  initializeTimePicker,
  initializeDateRangePicker,
  initializeDateTimePicker,
  initializeDateTimeFields,
  initializeDashboardDateRangePicker,
  initializeAllDayCheckbox,
  parseDate,
  formatDate,
};
